<template lang="pug">
.flex-col
  el-select(
    v-model="model",
    v-cancel-read-only,
    :clearable="clearable",
    filterable,
    remote,
    :remote-method="filter",
    :multiple="props.multiple",
    :placeholder="props.placeholder",
    placement="bottom",
    suffix-icon="ArrowDown",
    :remote-show-suffix="true",
    :disabled="props.disabled"
  )
    template(#prefix)
      font-awesome-icon(:icon="['fas', 'fa-magnifying-glass']")
    template(#empty) {{ $t("noData") }}
    el-option-group(v-lazy-load="loadMoreData")
      el-option(
        v-for="item in resultWithTemplateNames",
        :key="item.uuid",
        :value="item.uuid",
        :label="getLabel(item)"
      )
      el-option(v-if="loading", v-loading="true", :value="''", label="")

  TwinDetailsCompact.mt-2(
    v-if="showDetails && selectedItem",
    :id="selectedItem.uuid",
    @twin="(t) => emit('twinSelected', t)"
  )
</template>

<script setup lang="ts">
import type { PaginationQuery } from "@shared/services/common";
import { searchTwins, getTwin } from "@shared/services/twinManagement";
import { computed, onMounted, onUnmounted, ref, unref, watch } from "vue";
import type { TwinBase, TwinQuery } from "../../../services/twinManagement";
import type { TwinResponse } from "../../../services/twinManagement";
import type { Pagination } from "../../../services/common";
import vLazyLoad from "../../../directives/elSelectInfiniteScroll";
import { throttle, debounce } from "lodash";
import { useTwinStore } from "../../../stores/twinManagement";
import { cancelReadOnly as vCancelReadOnly } from "@shared/directives/select-readonly";
import TwinDetailsCompact from "./TwinDetailsCompact.vue";
import { useI18n } from "vue-i18n";

const props = withDefaults(
  defineProps<{
    query: TwinQuery;
    modelValue?: Array<TwinResponse | TwinBase | string> | null;
    multiple?: boolean;
    placeholder?: string;
    // emit an array of ids
    emitIds?: boolean;
    showDetails?: boolean;
    // Show details of selected twins, usually this element will use a reduced twin dataset `TwinBase` since it was created to select Twin Relations
    // In some cases more details are needed, if details is set, modelValue will be seen as a `TwinResponse`.
    // Note that TwinResponse and TwinBase can't be used interchangeably, since the backend uses different property names (templateId and templateUuid)
    details?: boolean;
    displayTemplateName?: boolean;
    // Properties to be shown in the title of the selection, used to differentiate items with the same name
    // Array of property customIds
    propsInLabel?: string[];
    disabled?: boolean;
    clearable?: boolean;
  }>(),
  {
    modelValue: null,
    emitIds: false,
    placeholder: undefined,
    clearable: true,
    propsInLabel: null,
  },
);

const result = ref<(TwinBase | TwinResponse)[]>([]);

const selectedTwins = ref<(TwinBase | TwinResponse)[]>([]);

const page = ref<Pagination>(null);
// NOTE: all data loading is happening in this component itself, since we need to support infinite scrolling and search across an arbitrary number of input field that are being rendered to the user
onMounted(() => {
  loadMoreData();
  // If data is set as twins, we can use them directly
  if (
    props.modelValue &&
    props.modelValue.length > 0 &&
    typeof props.modelValue[0] !== "string"
  ) {
    selectedTwins.value = props.modelValue as TwinBase[];
  } else {
    loadSelectedTwins();
  }
});

const { t } = useI18n();

watch(
  () => props.modelValue,
  () => {
    if (
      props.modelValue &&
      props.modelValue.length > 0 &&
      typeof props.modelValue[0] !== "string"
    ) {
      selectedTwins.value = props.modelValue as TwinBase[] | TwinResponse[];
      setResult(result.value);
    } else {
      loadSelectedTwins();
    }
  },
);

// twinSelected is emitted if `showDetails` is true, in which case the twin is loaded after selection and then emitted with all data
const emit = defineEmits(["update:modelValue", "twinSelected"]);

const pagination = ref<PaginationQuery>({
  page: 0,
  pageSize: 10,
});

const request = ref<TwinQuery>({
  showProperties: props.propsInLabel !== null || props.details,
});

const loading = ref(false);
const loaded = ref(false);
const error = ref(false);

const loadSearch = ref();

let abortController: AbortController | null = null;

async function loadMoreData(reload?: boolean) {
  // NOTE: if we already load the current search query, do not reload
  if (
    !reload &&
    request.value.search === loadSearch.value &&
    (loading.value || (loaded.value && !hasMorePages.value))
  ) {
    return;
  }
  // NOTE: search queries might take time depending on complexity, we have to make sure that we actually only retrieve the last request, thus prior requests have to be cancelled
  if (abortController !== null) {
    try {
      abortController.abort();
    } catch (e) {
      // noop
    }
    abortController = null;
  }
  const query = { ...props.query, ...request.value };
  loadSearch.value = request.value.search;
  loaded.value = false;
  loading.value = true;
  pagination.value.page++;
  abortController = new AbortController();
  const response = await searchTwins(pagination.value, query, {
    signal: abortController.signal,
  }).catch((e) => {
    if (e && e?.code === "ERR_CANCELED") {
      // We cancelled the request in favor of a new search query
      e.dontLog = true;
      throw e;
    } else {
      error.value = true;
      loading.value = false;
      loaded.value = true;
    }
    throw e;
  });
  // NOTE: if we load the first page of a search, we want to override all previous data.
  // This is important because a request might finish between being cancelled and a data reset by a new request, in which case its data will be in the result.
  // In case of a next page load, we have to merge the data
  if (pagination.value.page === 1) {
    setResult(mapResponse(response.data.result));
  } else {
    setResult([...result.value, ...mapResponse(response.data.result)]);
  }
  page.value = {
    total: response.data.total,
    pagesTotal: response.data.pagesTotal,
  };
  loading.value = false;
  loaded.value = true;
}

function setResult(twins: (TwinBase | TwinResponse)[]) {
  // Filter for uniqueness, note that this isn't very effective but okay for human possible scroll sizes, this makes sense since there might be items created while scrolling, resulting in differences in paging
  const ids = new Set();
  result.value = [...twins, ...selectedTwins.value].filter((item) => {
    if (!ids.has(item.uuid)) {
      ids.add(item.uuid);
      return true;
    }
    return false;
  });
}

function mapResponse(twins: TwinResponse[]) {
  if (props.details || props.showDetails || props.propsInLabel) {
    return twins;
  }
  return twins.map((item) => ({
    uuid: item.uuid,
    name: item.name,
    templateId: item.templateUuid,
  }));
}

async function loadSelectedTwins() {
  // Load the data of selected twins to show proper names, if set as trings, otherwise the data does not have to be loaded
  if (
    props.modelValue &&
    props.modelValue.length > 0 &&
    typeof props.modelValue[0] === "string"
  ) {
    const uuids = props.modelValue as string[];
    const response = await searchTwins(
      { page: 1, pageSize: 50 },
      { uuids, showProperties: props.propsInLabel !== null },
    );
    selectedTwins.value = response.data.result.map((item) => ({
      uuid: item.uuid,
      name: item.name,
      templateId: item.templateUuid,
    }));
    setResult(result.value);
  }
}

watch(
  () => props.query,
  (oldVal, newVal) => {
    // NOTE: this isn't pretty, but it avoids unwanted reloaded if the query object hasn't changed at all
    if (JSON.stringify(oldVal) === JSON.stringify(newVal)) {
      return;
    }
    pagination.value.page = 0;
    setResult([]);
    page.value.pagesTotal = null;
    loading.value = true;
    loadMoreData(true);
  },
);

const debouncedLoadData = debounce(loadMoreData, 300);

const search = ref<string>("");

watch(search, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    if (newValue !== "") {
      // reset the current data
      request.value.search = newValue;
      pagination.value.page = 0;
      setResult([]);
      if (page.value) {
        page.value.pagesTotal = null;
      }
      loading.value = true;
      debouncedLoadData();
    } else {
      // NOTE: backend search now return NO values if search is ""
      request.value.search = undefined;
      pagination.value.page = 0;
      setResult([]);
      if (page.value) {
        page.value.pagesTotal = null;
      }
      loading.value = true;
      loadMoreData();
    }
  }
});

function filter(input: string) {
  search.value = input;
}

const twinStore = useTwinStore();

const resultWithTemplateNames = computed(() => {
  if (props.displayTemplateName && twinStore.templatesLoadingState.loaded) {
    return result.value.map((item) => {
      const templateId = item["templateId"]
        ? item["templateId"]
        : item["templateUuid"];
      const template = twinStore.templatesLoadingState.result.find(
        (template) => template.uuid === templateId,
      );
      return { ...item, templateName: template.name };
    });
  }
  return result.value;
});

const hasMorePages = computed(() => {
  if (page.value.pagesTotal === null) {
    // inital load
    return true;
  }
  if (page.value !== null) {
    return page.value.pagesTotal > pagination.value.page;
  }
  return false;
});

const model = computed({
  get() {
    if (props.modelValue && props.modelValue.length > 0) {
      if (typeof props.modelValue[0] === "string") {
        return props.multiple ? props.modelValue : props.modelValue[0];
      }
      return props.multiple
        ? props.modelValue.map((item) => (item as TwinBase).uuid)
        : props.modelValue[0].uuid;
    } else {
      return null;
    }
  },
  set(value) {
    if (value !== undefined) {
      if (props.emitIds) {
        emit("update:modelValue", Array.isArray(value) ? value : [value]);
        return;
      }

      if (typeof value === "string") {
        const resultItem = result.value.find((result) => result.uuid === value);
        if (resultItem) {
          emit("update:modelValue", [resultItem]);
        } else {
          emit("update:modelValue", undefined);
        }
        return;
      }
      emit(
        "update:modelValue",
        value.map((item) =>
          result.value.find((result) => result.uuid === item),
        ),
      );
    } else {
      emit("update:modelValue", undefined);
    }
  },
});

function getLabel(twin: TwinResponse & { templateName: string }) {
  let label = "";

  if (props.displayTemplateName) {
    label += twin.templateName + " ";
  }

  label += twin.name;

  if (props.propsInLabel) {
    const labelDetails = props.propsInLabel
      .map(
        (customId) =>
          twin.properties?.find((prop) => prop.customId === customId)
            ?.propertyValue?.value as string,
      )
      .filter((item) => item !== undefined && item !== "");
    label = [label, ...labelDetails].join(", ");
  }
  return label;
}
// Show details
const selectedItem = computed(() =>
  result.value.find((r) => r.uuid === model.value),
);
</script>
